import { create } from 'zustand';
import type { StreamedGameState, GameLogEntry, ConnectionStatus, GameFullPayload } from '../types';

interface ViewerState {
  // Connection state
  connectionStatus: ConnectionStatus;
  viewerCount: number;

  // Game state
  gameState: StreamedGameState | null;
  gameEnded: boolean;
  noActiveGame: boolean;
  gameFull: GameFullPayload | null;

  // UI state
  activeTab: 'scoreboard' | 'playbyplay';

  // Actions
  setConnectionStatus: (status: ConnectionStatus) => void;
  setViewerCount: (count: number) => void;
  setGameState: (state: StreamedGameState) => void;
  addLogEntry: (entry: GameLogEntry) => void;
  setGameEnded: (ended: boolean) => void;
  setNoActiveGame: (noGame: boolean) => void;
  setGameFull: (payload: GameFullPayload | null) => void;
  setActiveTab: (tab: 'scoreboard' | 'playbyplay') => void;
  reset: () => void;
}

const initialState = {
  connectionStatus: 'connecting' as ConnectionStatus,
  viewerCount: 0,
  gameState: null,
  gameEnded: false,
  noActiveGame: false,
  gameFull: null as GameFullPayload | null,
  activeTab: 'playbyplay' as const,
};

export const useViewerStore = create<ViewerState>((set) => ({
  ...initialState,

  setConnectionStatus: (status) => set({ connectionStatus: status }),

  setViewerCount: (count) => set({ viewerCount: count }),

  setGameState: (state) => set({ gameState: state, gameEnded: false, noActiveGame: false }),

  addLogEntry: (entry) =>
    set((state) => {
      if (!state.gameState) return state;
      // Deduplicate: don't add if entry with this ID already exists
      if (state.gameState.gameLog.some((e) => e.id === entry.id)) {
        return state;
      }
      return {
        gameState: {
          ...state.gameState,
          gameLog: [...state.gameState.gameLog, entry],
        },
      };
    }),

  setGameEnded: (ended) => set({ gameEnded: ended }),

  setNoActiveGame: (noGame) => set({ noActiveGame: noGame }),

  setGameFull: (payload) => set({ gameFull: payload }),

  setActiveTab: (tab) => set({ activeTab: tab }),

  reset: () => set(initialState),
}));
